import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import ButtonSecondary from 'components/shared/ButtonSecondary';
import ButtonText from 'components/shared/ButtonText';
import Text from 'components/shared/Text';
import Heading from 'components/shared/Heading';
import SVG from 'components/icons/SVG';
import { useIsMobile } from 'helpers/hooks';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

const ModalPortal = props => {
  return ReactDOM.createPortal(<ModalWrapper {...props} />, document.getElementById('modal_hook'));
};

export function ModalWrapper(props) {
  const {
    handleClose,
    noClose,
    noForm,
    isAnimated,
    buttonLoading,
    buttonDisabled,
    buttonText,
    cancelButtonText,
    children,
    handleSubmit,
    handleGoBack,
    hasCancelButton,
    headerText,
    confirmationText,
    submitWarning
  } = props;
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const closeModal = useCallback(() => {
    if (isMobile) window.scrollTo({ top: 0 });
    handleClose();
  }, [handleClose, isMobile]);

  useEffect(() => {
    if (isMobile) {
      const microfrontend = document.getElementById('microfrontend_hook');
      microfrontend.classList.add('hidden');
      const footer = document.getElementById('footer_hook');
      footer.classList.add('hidden');
      const unverifiedBar = document.getElementById('unverified_bar_hook');
      if (unverifiedBar) unverifiedBar.classList.add('hidden');
    }
    return () => {
      const microfrontend = document.getElementById('microfrontend_hook');
      microfrontend.classList.remove('hidden');
      const footer = document.getElementById('footer_hook');
      footer.classList.remove('hidden');
      const unverifiedBar = document.getElementById('unverified_bar_hook');
      if (unverifiedBar) unverifiedBar.classList.remove('hidden');
    };
  }, [isMobile]);

  useEffect(() => {
    function onKeyDown(e) {
      if (!noClose) {
        if (e.key === 'Escape') {
          closeModal();
        }
      }
      if (noForm && !!handleSubmit && !buttonDisabled && !buttonLoading) {
        if (e.key === 'Enter') {
          e.preventDefault();
          closeModal();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [noClose, closeModal, noForm, handleSubmit, buttonDisabled, buttonLoading]);

  const submit = e => {
    e.preventDefault(e);
    if (!!handleSubmit && !buttonDisabled && !buttonLoading) handleSubmit();
  };

  const renderChildren = () => {
    return typeof children === 'function' ? children(closeModal) : children;
  };

  return (
    <div className={classnames([styles.container, { [styles.fadeIn]: isAnimated }])}>
      {/* eslint-disable-next-line */}
      <div className={styles.overlay} onClick={noClose ? () => {} : closeModal} />
      <div className={styles.dialog}>
        {!isMobile && !noClose && (
          <ButtonText onClick={closeModal} styles={[styles.buttonClose]}>
            <SVG icon="cross" muted />
          </ButtonText>
        )}

        {headerText && (
          <Heading tag="h3" styles={[styles.heading]}>
            {headerText}
          </Heading>
        )}

        {noForm ? (
          <div>{renderChildren()}</div>
        ) : (
          <form onSubmit={submit}>
            {renderChildren()}
            {/* Let users submit forms with Enter key */}
            <button type="submit" className={styles.hiddenButton}>
              Submit
            </button>
          </form>
        )}

        {confirmationText && (
          <div className={styles.autoMarginTop}>
            <Text tag="p" muted centered={isMobile}>
              {confirmationText}
            </Text>
          </div>
        )}
        <div className={classnames([styles.buttonContainer, { [styles.autoMarginTop]: !confirmationText }])}>
          {handleGoBack && (
            <ButtonSecondary type="button" onClick={handleGoBack} styles={[styles.buttonGoBack]}>
              {t('common_go_back')}
            </ButtonSecondary>
          )}

          {(hasCancelButton || cancelButtonText) && !handleGoBack && !noClose && isMobile && (
            <ButtonSecondary onClick={closeModal} styles={[styles.buttonCancel]}>
              {cancelButtonText || t('common_cancel')}
            </ButtonSecondary>
          )}

          {handleSubmit && (
            <ButtonPrimary
              onClick={submit}
              disabled={buttonDisabled}
              loading={buttonLoading}
              warning={submitWarning}
              styles={[styles.buttonSubmit]}>
              {buttonText || t('common_submit')}
            </ButtonPrimary>
          )}

          {(hasCancelButton || cancelButtonText) && !noClose && !isMobile && (
            <ButtonSecondary onClick={closeModal} styles={[styles.buttonCancel]}>
              {cancelButtonText || t('common_cancel')}
            </ButtonSecondary>
          )}
        </div>
      </div>
    </div>
  );
}

ModalWrapper.propTypes = {
  noClose: PropTypes.bool,
  noForm: PropTypes.bool,
  isAnimated: PropTypes.bool,
  headerText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  buttonLoading: PropTypes.bool,
  buttonDisabled: PropTypes.bool,
  buttonText: PropTypes.string,
  hasCancelButton: PropTypes.bool,
  cancelButtonText: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
  handleGoBack: PropTypes.func,
  children: PropTypes.any,
  confirmationText: PropTypes.string,
  submitWarning: PropTypes.bool
};

ModalWrapper.defaultProps = {
  noClose: false,
  noForm: false,
  isAnimated: true,
  headerText: null,
  buttonLoading: false,
  buttonDisabled: false,
  buttonText: null,
  hasCancelButton: false,
  cancelButtonText: null,
  handleSubmit: null,
  handleGoBack: null,
  children: null,
  confirmationText: null,
  submitWarning: false
};

export default ModalPortal;
